import axios from 'axios'
import isEmpty from 'lodash-es/isEmpty'
import { computed, defineComponent, onMounted, ref, toRef, watch } from 'vue'

import { CsModal, CsNotification, MsHeadline, useHeaderStore } from '@sunrise/components'
import { storeToRefs } from '@sunrise/pinia'
import { screenSizeDetection, sourcePoint, useGlobalStore } from '@sunrise/shared'
import { Components } from '@sunrise/types'

import { Bookmark } from '../../ts/mapper/bookmarkMapper.interface'
import { decoratedItemsOf } from '../../ts/mapper/decoratedEstateMapper'
import { vendorName } from '../../ts/sourcepoint/vendor.enum'
import { useBookmarkStore } from '../../ts/store/bookmarkStore/bookmarkStoreModule'
import { useToastStore } from '../../ts/store/toastStoreModule/toastStoreModule'

import MsBookmarkItem from '../MsBookmarkItem/MsBookmarkItem.vue'
import MsLocalToastManager from '../MsLocalToastManager/MsLocalToastManager.vue'
import { TextContent } from '../MsToastManager/MsToastManager.enum'
import { Toast } from '../MsToastManager/MsToastManager.interface'
import MsTooltip from '../MsTooltip/MsTooltip.vue'
import MsEmptyResultListSvg from './illus/empty-bookmarklist.svg?component'

export default defineComponent({
    name: 'MsBookmarkList',
    components: {
        CsModal,
        CsNotification,
        MsHeadline,
        MsTooltip,
        MsEmptyResultListSvg,
        MsBookmarkItem,
        MsLocalToastManager,
    },
    props: {
        componentId: {
            type: String,
            required: true,
        },
        baseUrl: String,
    },
    setup(props) {
        const headerStore = useHeaderStore()
        const bookmarkStore = useBookmarkStore()
        const globalStore = useGlobalStore()
        const toastStore = useToastStore()

        const onOpenModal = toRef(headerStore, 'modalOpen')
        const { bookmarks } = storeToRefs(bookmarkStore)
        const hasBookmarks = computed(() => !!bookmarkStore.getAmount)
        const baseUrl = props.baseUrl || globalStore.requestContext?.globalLinks.realEstateDetail

        const { isScreenM } = screenSizeDetection()
        const toasts = ref<Toast[]>([])
        const consentGiven = ref(false)
        const modalIsVisible = ref(onOpenModal.value)

        function getBookmarkLength() {
            return bookmarks.value.own.length + bookmarks.value.immo.length
        }

        watch(onOpenModal, async (value) => {
            if (!value) {
                modalIsVisible.value = false
                return
            }

            if (!consentGiven.value) {
                noConsentHandler()
                return
            }

            modalIsVisible.value = true

            const { own } = bookmarks.value

            if (isEmpty(own)) {
                return
            }

            const { data } = await axios.post(`${globalStore.urlPaths?.services}/expose-online`, {
                ids: own.map((item) => item.guid),
            })

            bookmarkStore.updateBookmarks(decoratedItemsOf(data, baseUrl))
        })

        function showToast(exposeeId: string) {
            const toast: Toast = {
                type: Components.CsToast.ToastType.POSITIVE,
                textContent: TextContent.REMOVED_CAN_REVERT,
                exposeeId,
            }
            toasts.value.push(toast)
        }

        function determinePosition(listPosition: number): number {
            const lastPositionOwnInventory = bookmarks.value.own.length || 100
            return lastPositionOwnInventory + listPosition
        }

        function onUpdate(newToasts: Toast[]) {
            toasts.value = newToasts
        }

        function onUpdateVisibility(isVisible: boolean) {
            headerStore.$patch({
                modalOpen: isVisible,
            })
            toasts.value = []
        }

        function noConsentHandler() {
            toastStore.addToast({
                type: Components.CsToast.ToastType.ERROR,
                textContent: TextContent.NOT_AVAILABLE,
            })
            headerStore.$patch({
                modalOpen: false,
            })
            modalIsVisible.value = false
        }

        onMounted(() => {
            sourcePoint.watchVendorConsent(vendorName.ms, (hasConsent) => {
                consentGiven.value = hasConsent
                if (!hasConsent) {
                    bookmarkStore.clear()
                }
            })
        })

        return {
            bookmarks,
            hasBookmarks,
            onUpdate,
            onUpdateVisibility,
            toasts,
            reverse,
            isScreenM,
            getBookmarkLength,
            showToast,
            determinePosition,
            modalIsVisible,
            infoState: Components.CsNotification.NotificationState.INFO,
        }
    },
})

function reverse(bookmarks: Bookmark[]) {
    return [...bookmarks].reverse()
}
